/**
 * Shown to drivers awaiting admin approval after registration.
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../authContext';
import { uploadDriverLicense } from '../lib/driverLicense';
import { licenseTypeLabel } from '../lib/transport';

const accent = '#0d7377';
const bg = '#0f1419';
const card = '#1a2332';
const text = '#e8eaed';
const muted = '#9aa0a6';

export default function DriverPending() {
  const { profile, signOutUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [licenseFile, setLicenseFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function handleSignOut() {
    await signOutUser();
    navigate('/login', { replace: true });
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!profile?.id || !licenseFile) return;
    setUploading(true);
    setError('');
    const { error: uploadErr } = await uploadDriverLicense(profile.id, licenseFile);
    setUploading(false);
    if (uploadErr) {
      setError(uploadErr.message);
      return;
    }
    setMessage('Licence uploaded. Your admin can review it when approving your application.');
    setLicenseFile(null);
    await refresh();
  }

  if (profile?.role === 'driver' && profile.approval_status === 'approved' && profile.active) {
    navigate('/driver', { replace: true });
    return null;
  }

  const rejected = profile?.approval_status === 'rejected';

  return (
    <div style={shell}>
      <div style={{ maxWidth: 440, width: '100%' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: 12 }}>
          {rejected ? 'Application not approved' : 'Awaiting approval'}
        </h1>
        <div style={cardBox}>
          {rejected ? (
            <p style={{ margin: 0, color: muted, lineHeight: 1.6 }}>
              Your driver registration was not approved. Contact your organisation admin if you believe this is a mistake.
            </p>
          ) : (
            <p style={{ margin: 0, color: muted, lineHeight: 1.6 }}>
              Thanks for registering as a driver. Your organisation admin must approve your account before you can sign in
              and be assigned a bus. You will receive access once approved — try signing in again after that.
            </p>
          )}
          {profile?.driver_license_type ? (
            <p style={{ margin: '16px 0 0', fontSize: '0.88rem', color: muted }}>
              Licence type: <strong style={{ color: text }}>{licenseTypeLabel(profile.driver_license_type)}</strong>
            </p>
          ) : null}
          {!rejected && !profile?.driver_license_file_path ? (
            <form onSubmit={handleUpload} style={{ marginTop: 16 }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: muted, marginBottom: 6 }}>
                Upload driving licence (missed at registration)
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={(e) => setLicenseFile(e.target.files?.[0] || null)}
                style={fileInput}
              />
              <button type="submit" disabled={!licenseFile || uploading} style={primaryBtn}>
                {uploading ? 'Uploading…' : 'Upload licence'}
              </button>
            </form>
          ) : !rejected && profile?.driver_license_file_path ? (
            <p style={{ margin: '16px 0 0', fontSize: '0.88rem', color: '#81c995' }}>Licence file uploaded — awaiting admin review.</p>
          ) : null}
          {message ? <p style={{ color: '#81c995', fontSize: '0.88rem', marginTop: 12, marginBottom: 0 }}>{message}</p> : null}
          {error ? <p style={{ color: '#f28b82', fontSize: '0.88rem', marginTop: 12, marginBottom: 0 }}>{error}</p> : null}
        </div>
        <p style={{ marginTop: 24 }}>
          <Link to="/login" style={{ color: accent, fontWeight: 600, textDecoration: 'none' }}>
            Back to sign in
          </Link>
        </p>
        <button type="button" onClick={handleSignOut} style={ghostBtn}>
          Sign out
        </button>
      </div>
    </div>
  );
}

const shell = {
  minHeight: '100vh',
  background: bg,
  color: text,
  fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  boxSizing: 'border-box',
};

const cardBox = {
  background: card,
  borderRadius: 14,
  padding: 20,
  border: '1px solid rgba(255,255,255,0.06)',
};

const ghostBtn = {
  marginTop: 16,
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'transparent',
  color: text,
  cursor: 'pointer',
};

const primaryBtn = {
  marginTop: 10,
  padding: '10px 14px',
  borderRadius: 10,
  border: 'none',
  background: `linear-gradient(180deg, ${accent}, #0a5c5f)`,
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};

const fileInput = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.12)',
  background: '#0f1419',
  color: text,
  fontSize: '0.9rem',
};
